import { useState, useRef, useCallback } from "react";
import {
  Upload,
  Link as LinkIcon,
  Loader2,
  Check,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useGetMediaQuery,
  useUploadMediaMutation,
  useImportMediaFromUrlMutation,
} from "@/store";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import type { Media, MediaFolder, ImageTransformOptions } from "@/types";
import { cn } from "@/lib/utils";

interface MediaSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (media: Media | Media[]) => void;
  multiple?: boolean;
  folder?: MediaFolder;
  title?: string;
  description?: string;
}

const FOLDERS: { value: MediaFolder | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "products", label: "Products" },
  { value: "categories", label: "Categories" },
  { value: "deals", label: "Deals" },
  { value: "general", label: "General" },
];

export function MediaSelector({
  open,
  onOpenChange,
  onSelect,
  multiple = false,
  folder,
  title = "Select Media",
  description = "Choose an image from your media library or upload a new one",
}: MediaSelectorProps) {
  const [activeTab, setActiveTab] = useState<"library" | "upload" | "url">("library");
  const [selectedFolder, setSelectedFolder] = useState<MediaFolder | "all">(folder || "all");
  const [selected, setSelected] = useState<Media[]>([]);
  const [page, setPage] = useState(1);
  const [uploadFolder, setUploadFolder] = useState<MediaFolder>(folder || "products");
  const [importUrl, setImportUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [transforms, setTransforms] = useState<ImageTransformOptions>({
    sharpen: false,
    upscale: false,
    enhance: false,
    removeBackground: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: mediaData, isLoading } = useGetMediaQuery(
    {
      folder: selectedFolder === "all" ? undefined : selectedFolder,
      page,
      limit: 24,
    },
    { skip: !open }
  );

  const [uploadMediaMutation, { isLoading: isUploading }] = useUploadMediaMutation();
  const [importMediaMutation, { isLoading: isImporting }] = useImportMediaFromUrlMutation();

  const media = mediaData?.data || [];
  const pagination = mediaData?.pagination;

  const handleSelect = (item: Media) => {
    if (multiple) {
      setSelected((prev) => {
        const isSelected = prev.some((m) => m.id === item.id);
        if (isSelected) {
          return prev.filter((m) => m.id !== item.id);
        }
        return [...prev, item];
      });
    } else {
      setSelected([item]);
    }
  };

  const handleConfirm = () => {
    if (selected.length === 0) {
      toast.error("Please select at least one image");
      return;
    }
    onSelect(multiple ? selected : selected[0]);
    handleClose();
  };

  const handleClose = () => {
    setSelected([]);
    setActiveTab("library");
    setImportUrl("");
    onOpenChange(false);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload an image.");
      return;
    }

    try {
      const result = await uploadMediaMutation({ 
        file, 
        folder: uploadFolder,
        transforms: transforms,
      }).unwrap();
      toast.success("Media uploaded successfully");
      // Auto-select the uploaded media
      if (result.data) {
        setSelected([result.data]);
        setActiveTab("library");
      }
      // Reset transforms
      setTransforms({ sharpen: false, upscale: false, enhance: false });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to upload media";
      toast.error(errorMessage);
    }
  };

  const handleImportFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      new URL(importUrl);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    try {
      const result = await importMediaMutation({ 
        url: importUrl, 
        folder: uploadFolder,
        transforms: transforms,
      }).unwrap();
      toast.success("Media imported successfully");
      // Auto-select the imported media
      if (result.data) {
        setSelected([result.data]);
        setActiveTab("library");
      }
      setImportUrl("");
      // Reset transforms
      setTransforms({ sharpen: false, upscale: false, enhance: false, removeBackground: false });
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to import media";
      toast.error(errorMessage);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileUpload(e.dataTransfer.files);
    },
    [uploadFolder]
  );

  const isSelected = (item: Media) => selected.some((m) => m.id === item.id);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="library">Media Library</TabsTrigger>
            <TabsTrigger value="upload">Upload</TabsTrigger>
            <TabsTrigger value="url">Import URL</TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="mt-4 flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Folder Filter */}
            <div className="flex items-center gap-2 mb-4">
              <Label className="text-sm">Folder:</Label>
              <div className="flex gap-1">
                {FOLDERS.map((f) => (
                  <Button
                    key={f.value}
                    variant={selectedFolder === f.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSelectedFolder(f.value);
                      setPage(1);
                    }}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Media Grid */}
            <ScrollArea className="flex-1 min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : media.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">No media found</p>
                  <Button size="sm" onClick={() => setActiveTab("upload")}>
                    Upload an image
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3 p-1">
                  {media.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "relative aspect-square rounded-lg border-2 overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-primary",
                        isSelected(item)
                          ? "border-primary ring-2 ring-primary"
                          : "border-transparent"
                      )}
                      onClick={() => handleSelect(item)}
                    >
                      <img
                        src={item.thumbnailUrl || item.url}
                        alt={item.filename}
                        className="h-full w-full object-cover"
                      />
                      {isSelected(item) && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <div className="bg-primary text-primary-foreground rounded-full p-1">
                            <Check className="h-4 w-4" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="mt-4 flex-1 overflow-y-auto scrollbar-hide">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Upload to folder</Label>
                <Select
                  value={uploadFolder}
                  onValueChange={(value) => setUploadFolder(value as MediaFolder)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">Products</SelectItem>
                    <SelectItem value="categories">Categories</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* AI Enhancements */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">AI Enhancements</Label>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="selector-enhance"
                      checked={transforms.enhance}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, enhance: checked }))
                      }
                    />
                    <Label htmlFor="selector-enhance" className="text-sm cursor-pointer">
                      AI Enhance
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="selector-upscale"
                      checked={transforms.upscale}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, upscale: checked }))
                      }
                    />
                    <Label htmlFor="selector-upscale" className="text-sm cursor-pointer">
                      Upscale
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="selector-sharpen"
                      checked={transforms.sharpen}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, sharpen: checked }))
                      }
                    />
                    <Label htmlFor="selector-sharpen" className="text-sm cursor-pointer">
                      Sharpen
                    </Label>
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-12 text-center transition-colors",
                  isDragging
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  id="media-selector-upload"
                />
                <label
                  htmlFor="media-selector-upload"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  {isUploading ? (
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  ) : (
                    <Upload className="h-12 w-12 text-muted-foreground" />
                  )}
                  <div>
                    <span className="text-lg font-medium">
                      {isUploading ? "Uploading..." : "Drag and drop or click to upload"}
                    </span>
                    <p className="text-sm text-muted-foreground mt-1">
                      PNG, JPG, GIF, WebP, SVG up to 10MB
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="url" className="mt-4 flex-1 overflow-y-auto scrollbar-hide">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Import to folder</Label>
                <Select
                  value={uploadFolder}
                  onValueChange={(value) => setUploadFolder(value as MediaFolder)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">Products</SelectItem>
                    <SelectItem value="categories">Categories</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="import-url">Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="import-url"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleImportFromUrl} disabled={isImporting}>
                    {isImporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <LinkIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* AI Enhancements */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">AI Enhancements</Label>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="url-enhance"
                      checked={transforms.enhance}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, enhance: checked }))
                      }
                    />
                    <Label htmlFor="url-enhance" className="text-sm cursor-pointer">
                      AI Enhance
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="url-upscale"
                      checked={transforms.upscale}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, upscale: checked }))
                      }
                    />
                    <Label htmlFor="url-upscale" className="text-sm cursor-pointer">
                      Upscale
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="url-sharpen"
                      checked={transforms.sharpen}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, sharpen: checked }))
                      }
                    />
                    <Label htmlFor="url-sharpen" className="text-sm cursor-pointer">
                      Sharpen
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="url-remove-background"
                      checked={transforms.removeBackground}
                      onCheckedChange={(checked) => 
                        setTransforms((prev) => ({ ...prev, removeBackground: checked }))
                      }
                    />
                    <Label htmlFor="url-remove-background" className="text-sm cursor-pointer">
                      Remove Background
                    </Label>
                  </div>
                </div>
              </div>

              {importUrl && (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Preview:</p>
                  <img
                    src={importUrl}
                    alt="Preview"
                    className="max-h-48 object-contain rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="shrink-0">
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-muted-foreground">
              {selected.length > 0
                ? `${selected.length} ${selected.length === 1 ? "image" : "images"} selected`
                : "No selection"}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={selected.length === 0}>
                {multiple ? `Select ${selected.length} Image${selected.length !== 1 ? "s" : ""}` : "Select Image"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
